import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { Client, JobPriority, Workflow, WorkflowStep } from '@workflow/contracts'
import {
  Button,
  ConfirmButton,
  Dropdown,
  EmptyNote,
  FADE,
  Field,
  Icons,
  IconButton,
  Loading,
  StateNote,
  TextArea,
  TextField,
} from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import {
  useAddWorkflowStep,
  useCreateWorkflow,
  useDeleteWorkflow,
  useInstantiateWorkflow,
  useJobTypes,
  useRemoveWorkflowStep,
  useReorderWorkflowSteps,
  useSession,
  useUpdateWorkflow,
  useUpdateWorkflowStep,
  useWorkflows,
} from '@/lib/api/hooks'
import { PosterDialog } from '@/shell/PosterDialog'
import { PRIORITY_LABEL, formatHours } from '@/features/jobs/jobsStatus'

const PRIORITY_OPTIONS = [
  { value: '', label: 'As the steps propose' },
  ...Object.entries(PRIORITY_LABEL).map(([value, label]) => ({ value, label: label as string })),
]

const CURRENCY_OPTIONS = [
  { value: 'BDT', label: 'BDT' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'GBP', label: 'GBP' },
  { value: 'INR', label: 'INR' },
]

/**
 * A client's workflows: the ordered templates their work opens from. Each
 * step is a kind of work carrying the defaults a job born from it starts
 * with; opening one opens a job per step, every job still editable
 * afterwards. Reading and opening are staff surfaces; shaping the template
 * is an administrator's — the API holds that line whatever the console shows.
 */
export function ClientWorkflows({ client }: { client: Client }) {
  const session = useSession()
  const isAdmin = session.data?.account?.role === 'admin'
  const workflows = useWorkflows(client.id)
  const [creating, setCreating] = useState(false)

  return (
    <div className="flex flex-col gap-4">
      {isAdmin ? (
        creating ? (
          <NewWorkflow clientId={client.id} onDone={() => setCreating(false)} />
        ) : (
          <div>
            <Button size="sm" onClick={() => setCreating(true)}>
              New workflow
            </Button>
          </div>
        )
      ) : null}

      {workflows.isLoading ? (
        <Loading label="Loading workflows" />
      ) : workflows.error ? (
        <StateNote title="The workflows did not load." detail={describeError(workflows.error)} />
      ) : (workflows.data?.length ?? 0) === 0 ? (
        <EmptyNote
          title="No workflows yet."
          detail={
            isAdmin
              ? 'Build the standing template this client\u2019s work opens from.'
              : 'An administrator can build the standing template this client\u2019s work opens from.'
          }
        />
      ) : (
        workflows.data!.map((workflow) => <WorkflowCard key={workflow.id} workflow={workflow} isAdmin={isAdmin} />)
      )}
    </div>
  )
}

/** The birth of a template: a name and its words; the steps join it after. */
function NewWorkflow({ clientId, onDone }: { clientId: string; onDone: () => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const create = useCreateWorkflow()

  return (
    <motion.form
      {...FADE}
      onSubmit={(event) => {
        event.preventDefault()
        create.mutate(
          { clientId, name: name.trim(), ...(description.trim() ? { description: description.trim() } : {}) },
          { onSuccess: onDone },
        )
      }}
      className="rounded-xl border bg-surface px-4 py-4 shadow-chip"
    >
      <p className="mb-3 text-micro uppercase text-muted-foreground">New workflow — for this client</p>
      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <Field label="Name" htmlFor="new-workflow-name" className="sm:col-span-2">
          <TextField
            id="new-workflow-name"
            value={name}
            placeholder="e.g. Harvest QA Round"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Description" htmlFor="new-workflow-description" className="sm:col-span-2">
          <TextArea
            id="new-workflow-description"
            rows={2}
            value={description}
            placeholder="What this standing round of work is (optional)"
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button type="submit" variant="primary" size="sm" busy={create.isPending} disabled={!name.trim()}>
          Create workflow
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {create.error ? <p className="mt-2 text-caption text-danger">{describeError(create.error)}</p> : null}
    </motion.form>
  )
}

/** One template: its steps in order, the work it opens, and — for an administrator — its shaping. */
function WorkflowCard({ workflow, isAdmin }: { workflow: Workflow; isAdmin: boolean }) {
  const [editing, setEditing] = useState(false)
  const [opening, setOpening] = useState(false)
  const remove = useDeleteWorkflow()
  const fault = isAdmin ? remove.error : undefined

  return (
    <div className="rounded-2xl border bg-surface px-5 py-4 shadow-chip">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-ui font-medium text-foreground">{workflow.name}</p>
          {workflow.description ? (
            <p className="mt-0.5 text-caption text-muted-foreground">{workflow.description}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="primary" onClick={() => setOpening(true)} disabled={workflow.steps.length === 0}>
            Open the work
          </Button>
          {isAdmin ? (
            <>
              <Button size="sm" onClick={() => setEditing((current) => !current)}>
                {editing ? 'Close' : 'Edit'}
              </Button>
              <ConfirmButton
                label="Delete"
                ask={{
                  title: `Delete ${workflow.name}?`,
                  detail: 'Its steps go with it. This cannot be undone.',
                  confirmLabel: 'Delete',
                }}
                busy={remove.isPending}
                onConfirm={() => remove.mutate({ id: workflow.id })}
              />
            </>
          ) : null}
        </div>
      </div>
      {fault ? <p className="mt-2 text-caption text-danger">{describeError(fault)}</p> : null}

      <AnimatePresence initial={false}>
        {editing && isAdmin ? (
          <motion.div {...FADE} className="mt-4">
            <EditWorkflow workflow={workflow} onDone={() => setEditing(false)} />
          </motion.div>
        ) : null}
      </AnimatePresence>

      <WorkflowSteps workflow={workflow} isAdmin={isAdmin} />
      {opening ? <InstantiateDialog workflow={workflow} onClose={() => setOpening(false)} /> : null}
    </div>
  )
}

/** The template's own words, editable in place. */
function EditWorkflow({ workflow, onDone }: { workflow: Workflow; onDone: () => void }) {
  const [name, setName] = useState(workflow.name)
  const [description, setDescription] = useState(workflow.description ?? '')
  const update = useUpdateWorkflow()

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        update.mutate(
          {
            id: workflow.id,
            input: {
              ...(name.trim() !== workflow.name ? { name: name.trim() } : {}),
              ...((description.trim() || null) !== workflow.description
                ? { description: description.trim() || null }
                : {}),
            },
          },
          { onSuccess: onDone },
        )
      }}
      className="rounded-xl border bg-surface-sunken px-4 py-4"
    >
      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <Field label="Name" htmlFor={`workflow-name-${workflow.id}`}>
          <TextField id={`workflow-name-${workflow.id}`} value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="Description" htmlFor={`workflow-description-${workflow.id}`}>
          <TextField
            id={`workflow-description-${workflow.id}`}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button type="submit" variant="primary" size="sm" busy={update.isPending} disabled={!name.trim()}>
          Save
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {update.error ? <p className="mt-2 text-caption text-danger">{describeError(update.error)}</p> : null}
    </form>
  )
}

/** The ordered steps, and — for an administrator — their shaping. */
function WorkflowSteps({ workflow, isAdmin }: { workflow: Workflow; isAdmin: boolean }) {
  const reorder = useReorderWorkflowSteps()
  const remove = useRemoveWorkflowStep()
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  /** One swap in the list, sent whole — the API renumbers in one batch. */
  const slide = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= workflow.steps.length) return
    const ids = workflow.steps.map((step) => step.id)
    ;[ids[index], ids[target]] = [ids[target]!, ids[index]!]
    reorder.mutate({ workflowId: workflow.id, ids })
  }

  return (
    <div className="mt-4 flex flex-col gap-1">
      {workflow.steps.length === 0 ? (
        <p className="text-caption text-muted-foreground">No steps yet — the workflow opens no work until it has some.</p>
      ) : (
        <ul className="flex flex-col">
          {workflow.steps.map((step, index) =>
            editingId === step.id ? (
              <li key={step.id} className="border-b border-hairline py-3 last:border-0">
                <EditStep workflow={workflow} step={step} onDone={() => setEditingId(null)} />
              </li>
            ) : (
              <li key={step.id} className="flex flex-wrap items-center gap-3 border-b border-hairline py-2.5 last:border-0">
                <span className="w-5 text-right font-mono text-caption text-muted-foreground">{index + 1}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-ui text-foreground">{step.title ?? step.typeName}</span>
                  <span className="block truncate text-caption text-muted-foreground">
                    {[
                      step.title ? step.typeName : null,
                      step.priority ? PRIORITY_LABEL[step.priority] : null,
                      step.estimatedHours === null ? null : formatHours(step.estimatedHours),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                {isAdmin ? (
                  <span className="flex items-center gap-1.5">
                    <span className="flex items-center">
                      <IconButton
                        label="Move up"
                        size="sm"
                        disabled={reorder.isPending || index === 0}
                        onClick={() => slide(index, -1)}
                        tooltipSide="top"
                      >
                        <span className="inline-flex -rotate-90">
                          <Icons.disclosure />
                        </span>
                      </IconButton>
                      <IconButton
                        label="Move down"
                        size="sm"
                        disabled={reorder.isPending || index === workflow.steps.length - 1}
                        onClick={() => slide(index, 1)}
                        tooltipSide="top"
                        className="-ml-1"
                      >
                        <span className="inline-flex rotate-90">
                          <Icons.disclosure />
                        </span>
                      </IconButton>
                    </span>
                    <Button size="xs" variant="ghost" onClick={() => setEditingId(step.id)}>
                      Edit
                    </Button>
                    <ConfirmButton
                      label="Remove"
                      confirmLabel="Confirm remove"
                      busy={remove.isPending}
                      onConfirm={() => remove.mutate({ workflowId: workflow.id, stepId: step.id })}
                    />
                  </span>
                ) : null}
              </li>
            ),
          )}
        </ul>
      )}

      {isAdmin ? (
        adding ? (
          <AddStep workflow={workflow} onDone={() => setAdding(false)} />
        ) : (
          <div className="mt-2">
            <Button size="sm" onClick={() => setAdding(true)}>
              Add step
            </Button>
          </div>
        )
      ) : null}
    </div>
  )
}

// ---- Step forms ----------------------------------------------------------------

/** A step joins at the end: the category of work it opens, and the defaults it proposes. */
function AddStep({ workflow, onDone }: { workflow: Workflow; onDone: () => void }) {
  const types = useJobTypes()
  const [typeId, setTypeId] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('')
  const [hours, setHours] = useState('')
  const add = useAddWorkflowStep()

  const typeOptions = (types.data ?? []).map((type) => ({ value: type.id, label: type.name }))

  return (
    <motion.form
      {...FADE}
      onSubmit={(event) => {
        event.preventDefault()
        add.mutate(
          {
            workflowId: workflow.id,
            input: {
              typeId,
              ...(title.trim() ? { title: title.trim() } : {}),
              ...(description.trim() ? { description: description.trim() } : {}),
              ...(priority ? { priority: priority as JobPriority } : {}),
              ...(hours.trim() !== '' && Number.isFinite(Number(hours)) ? { estimatedHours: Number(hours) } : {}),
            },
          },
          { onSuccess: onDone },
        )
      }}
      className="rounded-xl border bg-surface-sunken px-4 py-4"
    >
      <p className="mb-3 text-micro uppercase text-muted-foreground">New step — appended after the last</p>
      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <Field label="Category" htmlFor={`add-step-type-${workflow.id}`}>
          {types.isLoading ? (
            <Loading label="Loading kinds" />
          ) : (
            <Dropdown
              id={`add-step-type-${workflow.id}`}
              ariaLabel="Category"
              placeholder="Choose a category"
              value={typeId}
              options={typeOptions}
              onChange={setTypeId}
            />
          )}
        </Field>
        <Field label="Title" htmlFor={`add-step-title-${workflow.id}`} hint="The job title it proposes; the category's name stands in when left open.">
          <TextField
            id={`add-step-title-${workflow.id}`}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>
        <Field label="Priority" htmlFor={`add-step-priority-${workflow.id}`}>
          <Dropdown
            id={`add-step-priority-${workflow.id}`}
            ariaLabel="Priority"
            value={priority}
            options={PRIORITY_OPTIONS}
            onChange={setPriority}
          />
        </Field>
        <Field label="Estimated hours" htmlFor={`add-step-hours-${workflow.id}`}>
          <TextField
            id={`add-step-hours-${workflow.id}`}
            inputMode="decimal"
            placeholder="e.g. 6"
            value={hours}
            onChange={(event) => setHours(event.target.value === '' || Number.isFinite(Number(event.target.value)) ? event.target.value : hours)}
          />
        </Field>
        <Field label="Description" htmlFor={`add-step-description-${workflow.id}`} className="sm:col-span-2">
          <TextArea
            id={`add-step-description-${workflow.id}`}
            rows={2}
            value={description}
            placeholder="The words the job starts with (optional)"
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button type="submit" variant="primary" size="sm" busy={add.isPending} disabled={!typeId}>
          Add step
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {add.error ? <p className="mt-2 text-caption text-danger">{describeError(add.error)}</p> : null}
    </motion.form>
  )
}

/** Mid-edit on one step: absent means unchanged, null means cleared. */
function EditStep({ workflow, step, onDone }: { workflow: Workflow; step: WorkflowStep; onDone: () => void }) {
  const types = useJobTypes()
  const [typeId, setTypeId] = useState(step.typeId)
  const [title, setTitle] = useState(step.title ?? '')
  const [description, setDescription] = useState(step.description ?? '')
  const [priority, setPriority] = useState(step.priority ?? '')
  const [hours, setHours] = useState(step.estimatedHours === null ? '' : String(step.estimatedHours))
  const update = useUpdateWorkflowStep()

  // A retired kind keeps its name on the step that carries it, the way the
  // job picker does — the step still says its name, and saves still work.
  const typeOptions = (types.data ?? []).map((type) => ({ value: type.id, label: type.name }))
  if (step.title === null && !typeOptions.some((option) => option.value === step.typeId)) {
    typeOptions.push({ value: step.typeId, label: `${step.typeName} (retired)` })
  }

  const unchanged =
    typeId === step.typeId &&
    title.trim() === (step.title ?? '') &&
    description.trim() === (step.description ?? '') &&
    priority === (step.priority ?? '') &&
    (hours.trim() === '' ? null : Number(hours)) === step.estimatedHours

  return (
    <motion.form
      {...FADE}
      onSubmit={(event) => {
        event.preventDefault()
        update.mutate(
          {
            workflowId: workflow.id,
            stepId: step.id,
            input: {
              ...(typeId !== step.typeId ? { typeId } : {}),
              ...(title.trim() !== (step.title ?? '') ? { title: title.trim() || null } : {}),
              ...(description.trim() !== (step.description ?? '') ? { description: description.trim() || null } : {}),
              ...(priority !== (step.priority ?? '') ? { priority: priority === '' ? null : (priority as WorkflowStep['priority']) } : {}),
              ...((hours.trim() === '' ? null : Number(hours)) !== step.estimatedHours
                ? { estimatedHours: hours.trim() === '' ? null : Number(hours) }
                : {}),
            },
          },
          { onSuccess: onDone },
        )
      }}
      className="rounded-xl border bg-surface-sunken px-4 py-4"
    >
      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <Field label="Category" htmlFor={`edit-step-type-${step.id}`}>
          {types.isLoading ? (
            <Loading label="Loading kinds" />
          ) : (
            <Dropdown
              id={`edit-step-type-${step.id}`}
              ariaLabel="Category"
              value={typeId}
              options={typeOptions}
              onChange={setTypeId}
            />
          )}
        </Field>
        <Field label="Title" htmlFor={`edit-step-title-${step.id}`} hint="Left open, the category's name stands in.">
          <TextField id={`edit-step-title-${step.id}`} value={title} onChange={(event) => setTitle(event.target.value)} />
        </Field>
        <Field label="Priority" htmlFor={`edit-step-priority-${step.id}`}>
          <Dropdown
            id={`edit-step-priority-${step.id}`}
            ariaLabel="Priority"
            value={priority}
            options={PRIORITY_OPTIONS}
            onChange={setPriority}
          />
        </Field>
        <Field label="Estimated hours" htmlFor={`edit-step-hours-${step.id}`}>
          <TextField
            id={`edit-step-hours-${step.id}`}
            inputMode="decimal"
            value={hours}
            onChange={(event) => setHours(event.target.value === '' || Number.isFinite(Number(event.target.value)) ? event.target.value : hours)}
          />
        </Field>
        <Field label="Description" htmlFor={`edit-step-description-${step.id}`} className="sm:col-span-2">
          <TextArea
            id={`edit-step-description-${step.id}`}
            rows={2}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button type="submit" variant="primary" size="sm" busy={update.isPending} disabled={unchanged || !typeId}>
          Save
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {update.error ? <p className="mt-2 text-caption text-danger">{describeError(update.error)}</p> : null}
    </motion.form>
  )
}

/**
 * Opening the work: the fields every job born here shares, decided once.
 * The steps fill the rest — each job's kind, title, words, priority and
 * hours come from its step — and the money stays where it always lives,
 * on the jobs, decided per engagement.
 */
function InstantiateDialog({ workflow, onClose }: { workflow: Workflow; onClose: () => void }) {
  const [priority, setPriority] = useState('')
  const [playersNeeded, setPlayersNeeded] = useState('')
  const [currency, setCurrency] = useState('BDT')
  const instantiate = useInstantiateWorkflow()
  const players = (value: string) =>
    value.trim() === '' ? undefined : Number.isFinite(Number(value)) ? Math.max(0, Math.round(Number(value))) : undefined

  // The jobs open as drafts, like a hand-made one: no crew yet, so nobody —
  // players or Discord — hears of them until each is published from its page.
  const submit = () => {
    instantiate.mutate({
      workflowId: workflow.id,
      ...(priority ? { priority: priority as JobPriority } : {}),
      ...(playersNeeded.trim() ? { playersNeeded: players(playersNeeded) } : {}),
      currency,
    }, { onSuccess: onClose })
  }

  return (
    <PosterDialog
      open
      onClose={onClose}
      labelledBy="instantiate-workflow-headline"
      image="/art/onboarding-shoreline.png"
      colorArtwork
      alt="A blue shoreline painting with orange flowers"
      headline={workflow.name}
      caps={`Open the work — ${workflow.steps.length} ${workflow.steps.length === 1 ? 'job' : 'jobs'}, one per step`}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
        className="flex flex-col justify-center px-7 py-9 sm:px-9"
      >
        <p className="mb-5 text-micro uppercase text-muted-foreground">
          One job per step — {workflow.steps.map((step) => step.title ?? step.typeName).join(' → ')}
        </p>
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          <Field label="Priority" htmlFor="instantiate-priority" hint="A step's own proposal outranks this.">
            <Dropdown
              id="instantiate-priority"
              ariaLabel="Priority"
              value={priority}
              options={PRIORITY_OPTIONS}
              onChange={setPriority}
            />
          </Field>
          <Field label="Currency" htmlFor="instantiate-currency">
            <Dropdown
              id="instantiate-currency"
              ariaLabel="Currency"
              value={currency}
              options={CURRENCY_OPTIONS}
              onChange={setCurrency}
            />
          </Field>
          <Field label="Players" htmlFor="instantiate-players" hint="How many players each job needs.">
            <TextField
              id="instantiate-players"
              inputMode="numeric"
              placeholder="e.g. 5"
              value={playersNeeded}
              onChange={(event) => setPlayersNeeded(event.target.value === '' || /^\d+$/.test(event.target.value) ? event.target.value : playersNeeded)}
            />
          </Field>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <Button type="submit" variant="primary" size="md" busy={instantiate.isPending}>
            Draft {workflow.steps.length} {workflow.steps.length === 1 ? 'job' : 'jobs'}
          </Button>
          <Button type="button" size="md" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
        <p className="mt-3 text-caption text-muted-foreground">The jobs are saved as drafts. Assign players on each job's page, then publish it — players and Discord hear about it only then.</p>
        {instantiate.error ? (
          <p className="mt-2 text-caption text-danger">{describeError(instantiate.error)}</p>
        ) : null}
      </form>
    </PosterDialog>
  )
}
